import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { useShift } from "../contexts/ShiftContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Camera, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const STATUS_VARIANT = {
  active: "default",
  on_break: "secondary",
  completed: "outline",
  not_started: "outline",
};

const STATUS_LABEL = {
  active: "Working",
  on_break: "On break",
  completed: "Completed",
  not_started: "Not started",
};

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const { shift, fmtDuration } = useShift();
  const fileRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  // Change password state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
  }, [user]);

  useEffect(() => {
    api
      .get("/shift/history?days=14")
      .then(({ data }) => setHistory(data))
      .catch(() => {})
      .finally(() => setLoadingHist(false));
  }, []);

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo too large (max 3MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post("/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  const saveName = async () => {
    if (!name.trim() || name === user.name) return;
    setSavingName(true);
    try {
      await api.put("/profile", { name: name.trim() });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Update failed"
      );
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (savingPw) return;
    if (!currentPw || !newPw) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/profile/password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not change password"
      );
    } finally {
      setSavingPw(false);
    }
  };

  if (!user || user === false) return null;

  return (
    <div className="min-h-screen dp-grain" data-testid="profile-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 dp-fade-in space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            My Profile
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            {user.name || user.email}
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Profile info card */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="font-display">Profile details</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-6">
              <div className="relative shrink-0">
                <div className="h-28 w-28 rounded-full overflow-hidden ring-2 ring-border bg-muted">
                  {user.photo ? (
                    <img
                      src={user.photo}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full dp-gradient-bg flex items-center justify-center text-white text-3xl font-bold">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                  data-testid="profile-photo-input"
                />
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={uploading}
                  data-testid="profile-photo-btn"
                  className="absolute bottom-1 right-1 h-8 w-8 rounded-full dp-gradient-bg text-white flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </button>
              </div>

              <div className="flex-1 space-y-3 min-w-0">
                <div className="space-y-1.5">
                  <Label htmlFor="prof-name">Name</Label>
                  <div className="flex gap-2">
                    <Input
                      id="prof-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="profile-name-input"
                    />
                    <Button
                      onClick={saveName}
                      disabled={savingName || !name.trim() || name === user.name}
                      data-testid="profile-name-save-btn"
                      className="dp-gradient-bg text-white hover:opacity-90"
                    >
                      {savingName ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input value={user.email} disabled />
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Badge variant="secondary" className="capitalize">
                    {user.role}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Today's shift card */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Today</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <Badge
                  variant={STATUS_VARIANT[shift?.status || "not_started"]}
                  className="mt-1"
                  data-testid="profile-today-status"
                >
                  {STATUS_LABEL[shift?.status || "not_started"]}
                </Badge>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Net hours</div>
                <div className="font-display text-2xl font-bold tabular-nums">
                  {fmtDuration(shift?.net_minutes || 0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Break time</div>
                <div className="font-medium tabular-nums">
                  {fmtDuration(shift?.break_minutes || 0)}
                </div>
              </div>
              {shift?.punch_in_at && (
                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  Started {new Date(shift.punch_in_at).toLocaleTimeString()}
                  {shift.punch_out_at &&
                    ` · Ended ${new Date(shift.punch_out_at).toLocaleTimeString()}`}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Change password */}
        <Card data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Change password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={changePassword}
              className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
            >
              <div className="space-y-1.5">
                <Label htmlFor="cur-pw">Current password</Label>
                <Input
                  id="cur-pw"
                  type="password"
                  autoComplete="current-password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  data-testid="change-pw-current"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pw">New password</Label>
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  data-testid="change-pw-new"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw">Confirm new password</Label>
                <Input
                  id="confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  data-testid="change-pw-confirm"
                />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button
                  type="submit"
                  disabled={savingPw}
                  data-testid="change-pw-submit-btn"
                  className="dp-gradient-bg text-white hover:opacity-90"
                >
                  {savingPw ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Update password"
                  )}
                </Button>
              </div>
            </form>
            <p className="text-xs text-muted-foreground mt-3">
              Must be at least 6 characters. After updating, you'll stay logged
              in on this device.
            </p>
          </CardContent>
        </Card>

        {/* Recent shift history */}
        <Card data-testid="profile-history-card">
          <CardHeader>
            <CardTitle className="font-display">
              Recent shifts (last 14 days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Punch in</TableHead>
                  <TableHead>Punch out</TableHead>
                  <TableHead>Breaks</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingHist ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground py-10"
                    >
                      No shifts recorded yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.date}</TableCell>
                      <TableCell className="text-sm">
                        {row.punch_in_at
                          ? new Date(row.punch_in_at).toLocaleTimeString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.punch_out_at
                          ? new Date(row.punch_out_at).toLocaleTimeString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(row.breaks || []).length} · {fmtDuration(row.break_minutes)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {fmtDuration(row.net_minutes)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
